import ApiError from './ApiError';
import { AUTH_ENDPOINTS } from './authEndpoints';
import { emitApiUnauthorized } from './sessionEvents';

function isAbsoluteUrl(url) {
  return /^https?:\/\//i.test(url);
}

function trimSlashes(value = '') {
  return String(value).replace(/^\/+|\/+$/g, '');
}

function appendParams(url, params) {
  if (!params) {
    return url;
  }

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          searchParams.append(key, item);
        }
      });
      return;
    }

    searchParams.append(key, value);
  });

  const query = searchParams.toString();

  return query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url;
}

function buildUrl(baseUrl, path, params) {
  const normalizedPath = isAbsoluteUrl(path)
    ? path
    : [trimSlashes(baseUrl), trimSlashes(path)].filter(Boolean).join('/');
  const url = isAbsoluteUrl(normalizedPath) ? normalizedPath : `/${normalizedPath}`;

  return appendParams(url, params);
}

function isFormData(body) {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

function normalizeBody(body) {
  if (body === undefined || body === null || isFormData(body)) {
    return body;
  }

  return JSON.stringify(body);
}

function buildHeaders(headers, body) {
  const nextHeaders = {
    Accept: 'application/json',
    ...headers,
  };

  Object.keys(nextHeaders).forEach((key) => {
    if (nextHeaders[key] === undefined || nextHeaders[key] === null) {
      delete nextHeaders[key];
    }
  });

  if (body !== undefined && body !== null && !isFormData(body)) {
    nextHeaders['Content-Type'] = nextHeaders['Content-Type'] || 'application/json';
  }

  return nextHeaders;
}

async function parseResponseBody(response) {
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();

  return text || null;
}

function pickErrorMessage(response, payload) {
  const candidates = [
    payload?.msg,
    payload?.message,
    payload?.detail,
    response.statusText,
  ];

  return candidates.find((value) => typeof value === 'string' && value.trim()) || 'Request failed';
}

function buildApiError(response, payload) {
  return new ApiError({
    statusCode: response.status,
    message: pickErrorMessage(response, payload),
    detail: typeof payload?.detail === 'string' ? payload.detail : undefined,
    payload,
  });
}

function isSessionUnauthorizedResponse(response, payload) {
  return response.status === 401 && payload?.detail === 'Unauthorized';
}

function shouldRefreshUnauthorizedResponse({
  response,
  retryOnUnauthorized,
  skipAuthRefresh,
  isRetry,
  refreshPath,
}) {
  return response.status === 401
    && retryOnUnauthorized
    && !skipAuthRefresh
    && !isRetry
    && Boolean(refreshPath);
}

class ApiClient {
  constructor({ baseUrl = '', refreshPath = AUTH_ENDPOINTS.session.refreshJwt } = {}) {
    this._baseUrl = baseUrl;
    this._refreshPath = refreshPath;
    this._refreshPromise = null;
  }

  setBaseUrl(baseUrl) {
    this._baseUrl = baseUrl;
  }

  async request(path, options = {}) {
    const {
      method = 'GET',
      params,
      body,
      headers,
      credentials = 'include',
      auth = false,
      retryOnUnauthorized = auth,
      _isRetry = false,
      _skipAuthRefresh = false,
    } = options;
    const response = await fetch(buildUrl(this._baseUrl, path, params), {
      method,
      credentials,
      headers: buildHeaders(headers, body),
      body: normalizeBody(body),
    });
    let payload;

    try {
      payload = await parseResponseBody(response);
    } catch (error) {
      if (shouldRefreshUnauthorizedResponse({
        response,
        retryOnUnauthorized,
        skipAuthRefresh: _skipAuthRefresh,
        isRetry: _isRetry,
        refreshPath: this._refreshPath,
      })) {
        await this.refreshToken();

        return this.request(path, {
          ...options,
          _isRetry: true,
        });
      }

      throw error;
    }

    if (shouldRefreshUnauthorizedResponse({
      response,
      retryOnUnauthorized,
      skipAuthRefresh: _skipAuthRefresh,
      isRetry: _isRetry,
      refreshPath: this._refreshPath,
    })) {
      await this.refreshToken();

      return this.request(path, {
        ...options,
        _isRetry: true,
      });
    }

    if (!response.ok) {
      if (isSessionUnauthorizedResponse(response, payload)) {
        emitApiUnauthorized({
          path,
          statusCode: response.status,
        });
      }

      throw buildApiError(response, payload);
    }

    return payload;
  }

  refreshToken() {
    if (!this._refreshPath) {
      return Promise.reject(new ApiError({
        statusCode: 401,
        message: 'Token refresh is not configured for this API client.',
      }));
    }

    if (!this._refreshPromise) {
      this._refreshPromise = this.request(this._refreshPath, {
        method: 'POST',
        retryOnUnauthorized: false,
        _skipAuthRefresh: true,
      }).finally(() => {
        this._refreshPromise = null;
      });
    }

    return this._refreshPromise;
  }

  get(path, options) {
    return this.request(path, {
      ...options,
      method: 'GET',
    });
  }

  post(path, body, options) {
    return this.request(path, {
      ...options,
      method: 'POST',
      body,
    });
  }

  patch(path, body, options) {
    return this.request(path, {
      ...options,
      method: 'PATCH',
      body,
    });
  }

  delete(path, options) {
    return this.request(path, {
      ...options,
      method: 'DELETE',
    });
  }
}

export default ApiClient;
